import { CARD_DECK_IDS, DEFAULT_CARD_DECK, type CardDeckId } from "@bing/shared";
import { useRef, useSyncExternalStore } from "react";
import { CARD_DECKS } from "./card-decks";
import { deckReady, subscribeDecks } from "./deck-assets";

/**
 * A PICK TAKES EFFECT AT THE NEXT HAND — NEVER MID-HAND (the operator, 2026-10-20: «Switching to
 * the chosen deck: wait for the next hand, never mid-hand»).
 *
 * The deck a TABLE draws is decided once per hand, when the felt first draws it: the asked deck if
 * it is fully loaded at that moment (`deck-assets.ts`), «استاندارد» if not — and it then stays for
 * the whole hand, whatever happens meanwhile:
 *   - a deck that finishes downloading mid-hand waits for the next deal;
 *   - a deck CHANGE mid-hand (an online `settings.deck` in a later lobby frame) waits too;
 *   - a table that opens with the deck already in uses it from its first card;
 *   - a resume or reconnect into a mid-hand table without it plays that hand on «استاندارد».
 *
 * ⚠️ A HAND'S DECK IS ALWAYS ONE THAT CAN BE DRAWN (the batch re-review's N1, 2026-10-20). A hand
 * was latched «استاندارد» behind the loading screen, before the classic chunk was in; an image deck
 * that then landed first opened the gate onto «استاندارد» with nothing to draw it with, and `Card`
 * threw — the error screen took the table. So:
 *   - the containers start the hand only when the felt is drawn (`key` null until then: the gate is
 *     open, so the asked deck or «استاندارد» is drawable at that moment);
 *   - and whatever a caller passes, a hand only ever opens on a deck that is drawable now — the asked
 *     one, else «استاندارد», else the deck the previous hand had (an online felt stays up with no
 *     gate, M6: the host's new deck not here, the classic chunk failed). With NOTHING drawable it
 *     opens on an image deck that is not ready — `Card` draws each card's rank and suit until it is
 *     — never on «استاندارد» without its chunk, which `Card` refuses (it throws: a wiring bug).
 * ⚠️ ONE LOOK PER HAND, NEVER BLANK. A face that breaks mid-hand (an `<img>` error on a held face)
 * stops its deck being ready; the hand then goes to «استاندارد» for the REST OF THE HAND — the
 * re-fetch completing mid-hand does not switch it back (the re-review's minor). Without the classic
 * chunk the cards say their rank and suit meanwhile, and leave that for the first art that can be
 * drawn (the deck back, or «استاندارد» for the rest of the hand) — the one mid-hand change there is.
 *
 * «The next deal» is each game's own (`handKeyOf`): حکم and بی‌دل number their hands; شلم's view
 * has no number, but its dealer rotates on every deal, a redeal after four passes included; پاسور's
 * hand is the whole DECK (the operator's ruling: a 4-card refill is not a new hand — the table pool
 * stays face up across refills) — its DEAL where the view numbers them (a fresh deck per deal);
 * بلوف deals once per game.
 */
export type HandGame = "hokm" | "hearts" | "shelem" | "pasur" | "haftkhabis" | "bluff";

/** What changes in a view exactly when cards are dealt again — `null` before there is a view. */
export function handKeyOf(game: HandGame | string, view: unknown): string | null {
  if (view === null || view === undefined || typeof view !== "object") return null;
  const v = view as Record<string, unknown>;
  switch (game) {
    case "hokm":
    case "hearts":
      return `hand:${String(v["handNumber"])}`;
    case "shelem":
      return `dealer:${String(v["dealer"])}`;
    case "pasur":
      // THE HAND IS ONE DECK — the operator's ruling: a 4-card refill is not a new hand (the table
      // pool stays face up across refills). A view that NUMBERS its deals (`dealNumber`: a match
      // of several deals, each a fresh deck) keys on the deal, so a finished download applies at
      // the next fresh deck, solo and online alike; one without it (one deck per game: shuffled
      // once, the game over at its end) keys on the game, whose store starts with no view (`null`
      // above) — a fresh deck is a new game, and so a new hand.
      return typeof v["dealNumber"] === "number" ? `deal:${String(v["dealNumber"])}` : "deck";
    case "haftkhabis":
      // a match of rounds, each a fresh deck, numbered by the view
      return `round:${String(v["round"])}`;
    default:
      return "game"; // بلوف, and any game that deals once
  }
}

const isImageDeck = (id: CardDeckId): boolean => CARD_DECKS[id]?.faces != null;

/** Every deck's readiness (the classic chunk's is `standard`'s): the hook re-renders on any of them. */
const readiness = (): string => CARD_DECK_IDS.map((id) => (deckReady(id) ? "1" : "0")).join("");

/** The deck a hand OPENS on — drawable now if anything is (see the header). */
function openingDeck(asked: CardDeckId, previous: CardDeckId | null): CardDeckId {
  if (deckReady(asked)) return asked;
  if (deckReady(DEFAULT_CARD_DECK)) return DEFAULT_CARD_DECK;
  if (previous !== null && deckReady(previous)) return previous;
  // nothing drawable: rank and suit, under a deck that is not ready (never `standard`: Card throws)
  if (isImageDeck(asked)) return asked;
  if (previous !== null && isImageDeck(previous)) return previous;
  return DEFAULT_CARD_DECK; // only a table mounted without its gate gets here: Card says so, loudly
}

/**
 * The deck to PROVIDE to a table's cards: one per hand (see the header). `key` is the hand
 * (`handKeyOf`), `null` while there is none — before the felt is drawn — and then it follows the
 * deck as it is.
 */
export function useHandDeck(asked: CardDeckId, key: string | null): CardDeckId {
  useSyncExternalStore(subscribeDecks, readiness, readiness);
  // Written during render, and idempotent: each value is a function of (this hand's key, the decks'
  // state when it was taken) — a render React throws away recomputes the same one.
  const hand = useRef<{ key: string; deck: CardDeckId } | null>(null);
  // the deck the last hand ended on — a later hand's fallback; kept across a `null` (a rematch)
  const previous = useRef<CardDeckId | null>(null);
  if (key === null) {
    hand.current = null;
    return openingDeck(asked, previous.current);
  }
  if (hand.current === null || hand.current.key !== key)
    hand.current = { key, deck: openingDeck(asked, previous.current) };
  else if (!deckReady(hand.current.deck) && deckReady(DEFAULT_CARD_DECK))
    // this hand's deck stopped being drawable (a face broke) or never was: «استاندارد» from here to
    // the end of the hand — the re-fetch completing does not switch it back
    hand.current = { key, deck: DEFAULT_CARD_DECK };
  previous.current = hand.current.deck;
  return hand.current.deck;
}
